import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache pricing in-memory so multiple badges on the same page don't hammer the API
let _cache = null;
let _inflight = null;

const fetchPricing = async () => {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${API}/pricing/qs-check`)
    .then(r => {
      _cache = r.data;
      _inflight = null;
      return r.data;
    })
    .catch(() => {
      _inflight = null;
      return { price: 199, launch_price: 199, standard_price: 249, launch_active: true };
    });
  return _inflight;
};

export const useQSPricing = () => {
  const [pricing, setPricing] = useState(_cache);
  useEffect(() => {
    let mounted = true;
    fetchPricing().then(p => {
      if (mounted) setPricing(p);
    });
    return () => { mounted = false; };
  }, []);
  return pricing || { price: 199, launch_price: 199, standard_price: 249, launch_active: true };
};

/**
 * QSPriceBadge — single source of truth for QS Check pricing display across all CTAs.
 *
 * Variants:
 *   - 'large'  : Used as the headline price on upsell cards (big £199 with £249 crossed out above)
 *   - 'inline' : Used inside button labels, row items, compact spaces
 *   - 'button' : Just the price string for use inside button text
 *
 * When launch_active=false, only the standard price shows.
 */
export const QSPriceBadge = ({ variant = 'large', className = '', subtextClassName = '' }) => {
  const p = useQSPricing();
  const showLaunch = p.launch_active && p.launch_price < p.standard_price;

  if (variant === 'button') {
    return <>£{p.price}</>;
  }

  if (variant === 'inline') {
    if (!showLaunch) return <span className={className} data-testid="qs-price-inline">£{p.price}</span>;
    return (
      <span className={className} data-testid="qs-price-inline">
        <span style={{ textDecoration: 'line-through', opacity: 0.55, marginRight: 6, fontWeight: 500 }}>£{p.standard_price}</span>
        <strong>£{p.launch_price}</strong>
      </span>
    );
  }

  // variant === 'large'
  if (!showLaunch) {
    return (
      <div className={className} data-testid="qs-price-large">
        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--ss-navy, #0F2742)', lineHeight: 1 }}>£{p.price}</div>
      </div>
    );
  }
  return (
    <div className={className} data-testid="qs-price-large">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 18, fontWeight: 500, color: '#94A3B8', textDecoration: 'line-through' }}>£{p.standard_price}</span>
        <span style={{ fontSize: 28, fontWeight: 800, color: 'var(--ss-navy, #0F2742)', lineHeight: 1 }}>£{p.launch_price}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
          background: '#FEF3C7', color: '#92400E', padding: '3px 8px', borderRadius: 999,
        }}>Launch offer</span>
      </div>
      <div className={subtextClassName} style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
        Full price £{p.standard_price} after launch period
      </div>
    </div>
  );
};

/**
 * Shared QS Check feature list — updated to the 6-point spec (April 2026)
 * Used in: upsell cards across the app.
 */
export const QS_CHECK_FEATURES = [
  'Quantity verification',
  'Rate sense-check',
  'Assumptions and exclusions report',
  'Specification notes',
  'Contractor guidance',
  'QS-verified re-issue of documents',
];
